import type { FileRecord } from "@cohesive/api-client";
import {
  fileRecordVersionKey,
  pickFileRecordMediaURL,
  useStickyMediaSrc,
} from "@cohesive/ui-components";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

export function VideoContent({ record }: { record: FileRecord }) {
  const { previewMediaUrl } = useFileRecordVersionPreview();
  const mediaUrl =
    previewMediaUrl ??
    pickFileRecordMediaURL(record, "full") ??
    pickFileRecordMediaURL(record, "thumbnail");
  // Playing keeps its link until that link fails, then swaps to the renewed
  // one and resumes where it was.
  const { src, onError, ref, takeResumeTime } =
    useStickyMediaSrc<HTMLVideoElement>(mediaUrl);

  if (!mediaUrl) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Video not available.
      </div>
    );
  }

  const promptText = record.generation_prompt;
  const hasPrompt = Boolean(promptText);
  const showMetaFooter = hasPrompt;

  // Generated videos are often served as fragmented/streamed files whose
  // duration metadata isn't known until the browser scans to the end. Until
  // then `duration` is `Infinity` and the native scrubber parks itself near the
  // end even though `currentTime` is 0. Forcing a seek to the end makes the
  // browser resolve the real duration; we then rewind to the start so playback
  // (and the scrubber) begin where the user expects.
  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const resumeAt = takeResumeTime();
    if (!Number.isFinite(video.duration)) {
      const onTimeUpdate = () => {
        video.removeEventListener("timeupdate", onTimeUpdate);
        video.currentTime = resumeAt;
      };
      video.addEventListener("timeupdate", onTimeUpdate);
      video.currentTime = 1e101;
    } else if (video.currentTime !== resumeAt) {
      video.currentTime = resumeAt;
    }
  };

  return (
    <div className="flex h-full w-full min-h-0 flex-col bg-n-95">
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <video
          key={previewMediaUrl ?? fileRecordVersionKey(record)}
          ref={ref}
          src={src}
          controls
          controlsList="nodownload"
          className="max-h-full max-w-full"
          preload="auto"
          autoPlay
          muted
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
          onError={onError}
        />
      </div>
      {showMetaFooter && (
        <div className="shrink-0 border-t border-white/10 bg-background/95 backdrop-blur-sm">
          <div className="p-4 space-y-3 max-h-48 overflow-y-auto">
            {hasPrompt && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">
                  Prompt
                </h3>
                <p className="text-sm text-muted-foreground italic leading-relaxed whitespace-pre-wrap select-text">
                  &quot;{promptText}&quot;
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
